import React from 'react'
import AntSwitch from '@components/AntSwitch'
import {
  Cancel,
  CheckCircle,
  ErrorOutline as ErrorOutlineIcon,
  KeyboardArrowDown,
} from '@mui/icons-material'
import { Box, Collapse, Grid, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'next-i18next'

function FeatureIcon({ check }) {
  if (typeof check !== 'boolean') return <ErrorOutlineIcon color="primary" />
  return check ? (
    <CheckCircle sx={{ color: 'success.dark', transform: 'scaleX(1)!important' }} />
  ) : (
    <Cancel sx={{ color: 'error.dark' }} />
  )
}

function PlanCard({ plan, featurePlans, mainSection, checkedState }) {
  const { t } = useTranslation()
  const [checked, setChecked] = checkedState
  const [opened, setOpened] = React.useState(false)
  return (
    <Paper>
      <Box sx={{ p: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ pb: 4 }}
        >
          <Typography
            variant="body2"
            fontWeight={400}
            textTransform="uppercase"
          >
            {plan.name_text}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <AntSwitch
              inputProps={{ 'aria-label': 'Billing' }}
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <Typography
              variant="caption"
              fontSize={14}
              fontWeight={400}
              sx={{ pt: 0.25 }}
              textTransform="capitalize"
              display="block"
            >
              {t('common:yearly')}
            </Typography>
          </Stack>
        </Stack>
        <Typography
          variant="h4"
          fontWeight={700}
          gutterBottom
          display="block"
        >
          {plan.amount}{' '}
          <Typography
            variant="body2"
            fontWeight={500}
            component="span"
          >
            {plan.currency}/{plan.periodicity_type}
          </Typography>
        </Typography>
        {mainSection?.features?.map((feature) => (
          <Typography
            key={feature.name}
            variant="body2"
            fontSize={13}
            fontWeight={400}
            color="primary.main"
            display="block"
          >
            {feature.name_text}{' '}
            <Typography
              variant="body2"
              fontSize={15}
              fontWeight={700}
              component="span"
            >
              {feature.plans[plan.name]}
            </Typography>
          </Typography>
        ))}
      </Box>
      <Collapse in={opened}>
        {featurePlans.map(({ title, features }, index) => (
          <Box key={index}>
            <Box sx={{ backgroundColor: '#F9F9F9', pl: 4, py: 2 }}>{title}</Box>
            <Box
              py={2}
              pl={4}
              pr={2}
            >
              {features.map((feature, idx) => (
                <Stack
                  key={idx}
                  direction="row"
                  alignItems="flex-start"
                  spacing={1.25}
                  sx={{ py: 1 }}
                >
                  <FeatureIcon check={feature.plans[plan.name]} />
                  <Box sx={{ pt: 0.2 }}>
                    <Typography
                      variant="caption"
                      display="inline"
                    >
                      {feature.name_text}
                    </Typography>{' '}
                    {typeof feature.plans[plan.name] !== 'boolean' && (
                      <Typography
                        color="primary.main"
                        variant="caption"
                        display="inline"
                      >
                        {feature.plans[plan.name] >= 999999
                          ? t('pricing:Unlimited')
                          : feature.plans[plan.name]}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}
            </Box>
          </Box>
        ))}
      </Collapse>
      <Box
        px={4}
        py={2}
        onClick={() => setOpened(!opened)}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
        >
          <Typography
            variant="caption"
            fontWeight={700}
            textTransform="capitalize"
          >
            {t('pricing:show_features')}
          </Typography>
          <KeyboardArrowDown
            sx={{
              transition: (theme) =>
                theme.transitions.create(['transform'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
              transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Stack>
      </Box>
    </Paper>
  )
}
function RegularNarrow({ plans: allPlans, featurePlans: allFeaturePlans }) {
  const [checked, setChecked] = React.useState(true)

  const plans = [...(allPlans ?? [])]
    ?.sort((a, b) => a.amount - b.amount)
    ?.filter(
      (plan) =>
        (checked ? plan.periodicity_type === 'Year' : plan.periodicity_type === 'Month') ||
        plan.name === 'free'
    )
  const featurePlans = allFeaturePlans?.filter((plan) => plan.title !== 'Main')
  const mainSection = allFeaturePlans?.filter((plan) => plan.title === 'Main')?.[0]

  return (
    <Grid
      container
      spacing={4}
    >
      {plans.map((plan, index) => (
        <Grid
          item
          xs={12}
          key={index}
        >
          <PlanCard
            plan={plan}
            featurePlans={featurePlans}
            mainSection={mainSection}
            checkedState={[checked, setChecked]}
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default RegularNarrow
